/**
 * Promise-based confirm dialog to replace the ~40 native window.confirm()
 * calls (which are unstyled, untranslatable, and break RTL). Mount
 * <ConfirmProvider> once near the root, then:
 *
 *   const confirm = useConfirm();
 *   if (await confirm({ message: t('...'), tone: 'danger' })) doThing();
 *
 * Built on <Modal> so it inherits focus-trap + Escape + bottom-sheet.
 */
import {
	type ReactNode,
	createContext,
	useCallback,
	useContext,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./Button.tsx";
import { Modal } from "./Modal.tsx";

interface ConfirmOptions {
	title?: string;
	message: string;
	confirmLabel?: string;
	cancelLabel?: string;
	tone?: "danger" | "default";
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function ConfirmProvider({ children }: { children: ReactNode }) {
	const { t } = useTranslation("common");
	const [opts, setOpts] = useState<ConfirmOptions | null>(null);
	const resolver = useRef<((v: boolean) => void) | null>(null);

	const confirm = useCallback<ConfirmFn>((options) => {
		setOpts(options);
		return new Promise<boolean>((resolve) => {
			resolver.current = resolve;
		});
	}, []);

	const settle = (value: boolean) => {
		resolver.current?.(value);
		resolver.current = null;
		setOpts(null);
	};

	return (
		<ConfirmContext.Provider value={confirm}>
			{children}
			{opts && (
				<Modal open onClose={() => settle(false)} size="sm" title={opts.title}>
					<p className="text-sm text-muted-foreground leading-relaxed">
						{opts.message}
					</p>
					<div className="mt-5 flex items-center justify-end gap-2">
						<Button variant="ghost" onClick={() => settle(false)}>
							{opts.cancelLabel ?? t("actions.cancel")}
						</Button>
						<Button
							variant={opts.tone === "danger" ? "danger" : "primary"}
							onClick={() => settle(true)}
						>
							{opts.confirmLabel ?? t("actions.confirm")}
						</Button>
					</div>
				</Modal>
			)}
		</ConfirmContext.Provider>
	);
}

/** Returns an async confirm() — falls back to window.confirm if no provider. */
export function useConfirm(): ConfirmFn {
	const ctx = useContext(ConfirmContext);
	return (
		ctx ??
		((opts: ConfirmOptions) => Promise.resolve(window.confirm(opts.message)))
	);
}
